import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowRight, ArrowLeft, ChevronRight } from 'lucide-react';
import './immersive.css';

const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
const smooth = (v: number) => { const t = clamp(v); return t * t * (3 - 2 * t); };
const offers = [
  {
    "title": "Operator Risk Brief",
    "label": "2–4 week assessment",
    "text": "An executive risk and governance review that turns scattered concerns into a prioritized 90-day plan, named owners, and a defensible 12-month roadmap.",
    "href": "/cybersecurity-risk-assessment"
  },
  {
    "title": "Fractional CISO",
    "label": "Advisory or managed",
    "text": "Senior cybersecurity judgment, operating cadence, investment guidance, and executive reporting at the level of commitment your business actually needs.",
    "href": "/fractional-ciso"
  },
  {
    "title": "Managed Defense",
    "label": "Evidence before products",
    "text": "Assessment-led email, identity, endpoint, monitoring, and response capabilities—implemented with clear provider roles and governed as part of your program.",
    "href": "/#contact"
  },
  {
    "title": "Secure AI & Agent Defense",
    "label": "Governance + technical controls",
    "text": "Adopt AI agents with bounded permissions, protected data, human control, observable actions, and defenses built for AI-amplified attacks and fraud.",
    "href": "/ai-agent-security"
  }
];

export default function ImmersiveHero({ header }: { header: ReactNode }) {
  const root = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const jumpRef = useRef<(value: number) => void>(() => {});
  const [phase, setPhase] = useState(0);
  const [active, setActive] = useState(0);
  const [staticMode, setStaticMode] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);
  const chosen = useRef(0);
  const loaded = useRef(new Set<string>());
  const registerImage = (name: string) => { loaded.current.add(name); if (loaded.current.size === 2) setImagesReady(true); };

  useEffect(() => {
    const runway = root.current!;
    const view = stage.current!;
    // Short landscape screens need normal reading flow rather than a pinned panel.
    const media = matchMedia('(prefers-reduced-motion: reduce), (max-height: 500px) and (max-width: 950px)');
    const pointerMedia = matchMedia('(hover: hover) and (pointer: fine)');
    const world = view.querySelector<HTMLElement>('.immersive-world')!;
    const portal = view.querySelector<HTMLElement>('.immersive-portal')!;
    const wings = [...view.querySelectorAll<HTMLElement>('.immersive-wing')];
    const opening = view.querySelector<HTMLElement>('.immersive-opening')!;
    const bridge = view.querySelector<HTMLElement>('.immersive-bridge')!;
    const arrival = view.querySelector<HTMLElement>('.immersive-arrival')!;
    const cards = [...view.querySelectorAll<HTMLElement>('.immersive-card')];
    const deck = view.querySelector<HTMLElement>('.immersive-deck')!;
    let raw = { x: 0, y: 0 }, mouse = { x: 0, y: 0 }, frame = 0;
    let visible = true, lastPhase = -1, lastActive = -1, progress = 0;
    let manualIndex: number | null = null;
    let started = performance.now();
    const reduced = () => media.matches;
    const syncMode = () => { setStaticMode(reduced()); runway.dataset.static = String(reduced()); schedule(); };
    const show = (el: HTMLElement, opacity: number) => {
      el.style.opacity = String(opacity);
      el.style.visibility = opacity < .01 ? 'hidden' : 'visible';
      el.inert = opacity < .5;
    };
    const render = () => {
      frame = 0;
      if (!visible || document.hidden) return;
      const rect = runway.getBoundingClientRect();
      const travel = runway.offsetHeight - innerHeight;
      progress = reduced() ? 0 : clamp(-rect.top / Math.max(1, travel));
      mouse.x += (raw.x - mouse.x) * .07; mouse.y += (raw.y - mouse.y) * .07;
      const mx = reduced() ? 0 : -mouse.x, my = reduced() ? 0 : -mouse.y;
      const zoom = smooth(progress / .72);
      const openingOpacity = reduced() ? 1 : 1 - smooth((progress - .08) / .16);
      const bridgeOpacity = reduced() ? 0 : smooth((progress - .27) / .11) * (1 - smooth((progress - .51) / .12));
      const arrivalOpacity = reduced() ? 1 : smooth((progress - .63) / .1);
      const portalOpacity = 1 - smooth((progress - .53) / .18);
      world.style.transform = `scale(${1.025 + zoom * .18}) translate(${mx * 6}px,${my * 6}px)`;
      portal.style.transform = `scale(${1 + zoom * 5.7}) translate(${mx * 7}px,${my * 7}px)`;
      portal.style.opacity = String(reduced() ? .6 : portalOpacity);
      const intro = reduced() ? 1 : smooth((performance.now() - started) / 1600);
      wings.forEach((wing, i) => { wing.style.transform = `translateX(${(i ? 1 : -1) * (intro * 15 + zoom * 115)}%) translate(${mx * 14}px,${my * 4}px) scale(${1 + zoom * .16})`; wing.style.opacity = String(reduced() ? 0 : 1 - smooth(progress / .6)); });
      show(opening, openingOpacity); show(bridge, bridgeOpacity); show(arrival, arrivalOpacity);
      show(deck, arrivalOpacity);
      const p = progress < .24 ? 0 : progress < .63 ? 1 : 2;
      if (p !== lastPhase) { lastPhase = p; setPhase(p); }
      const sweep = manualIndex ?? clamp((progress - .73) / .25) * 3;
      const cardIndex = Math.round(sweep);
      if (cardIndex !== lastActive) { lastActive = cardIndex; chosen.current = cardIndex; setActive(cardIndex); }
      const mobile = innerWidth < 768;
      const radius = mobile ? 680 : 1300;
      const gap = mobile ? 24 : 15;
      cards.forEach((card, i) => {
        const deg = (i - sweep) * gap;
        const rad = deg * Math.PI / 180;
        const x = Math.sin(rad) * radius;
        const y = radius * (1 - Math.cos(rad));
        card.style.transform = reduced() ? '' : `translate(calc(-50% + ${x}px), ${y}px) rotate(${deg}deg)`;
        card.style.opacity = reduced() || i === cardIndex ? '1' : String(clamp(1 - Math.abs(i - sweep) * (mobile ? .65 : .19), .2));
        card.inert = !reduced() && (arrivalOpacity < .95 || (mobile && i !== cardIndex));
        card.dataset.active = String(i === cardIndex);
        card.style.zIndex = i === cardIndex ? '3' : '1';
      });
      if (reduced()) delete view.dataset.scVerifyState;
      else view.dataset.scVerifyState = `world:${(1.025+zoom*.18).toFixed(3)};portal:${(1+zoom*5.7).toFixed(2)};opacity:${portalOpacity.toFixed(2)};cards:${sweep.toFixed(2)};top:${Math.round(view.getBoundingClientRect().top)}`;
      const unsettled = Math.abs(raw.x-mouse.x) + Math.abs(raw.y-mouse.y) > .004;
      if ((!reduced() && performance.now() - started < 1700) || unsettled) schedule();
    };
    function schedule() { if (!frame) frame = requestAnimationFrame(render); }
    const onScroll = () => { manualIndex = null; schedule(); };
    const move = (event: PointerEvent) => { if (!pointerMedia.matches || reduced()) return; raw = { x: (event.clientX / innerWidth - .5) * 2, y: (event.clientY / innerHeight - .5) * 2 }; schedule(); };
    const leave = () => { raw = { x: 0, y: 0 }; schedule(); };
    const jump = (value: number) => {
      if (reduced()) { manualIndex = value >= .73 ? Math.round(clamp((value - .73) / .25) * 3) : 0; chosen.current = manualIndex; setActive(manualIndex); return; }
      const top = runway.getBoundingClientRect().top + scrollY;
      window.scrollTo({top: top + value * (runway.offsetHeight - innerHeight), behavior: 'instant'});
      schedule();
    };
    jumpRef.current = jump;
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; if (visible) schedule(); else delete view.dataset.scVerifyState; });
    observer.observe(runway);
    syncMode();
    addEventListener('scroll', onScroll, { passive: true }); addEventListener('resize', schedule);
    view.addEventListener('pointermove', move, { passive: true });view.addEventListener('pointerleave', leave);
    document.addEventListener('visibilitychange', schedule);media.addEventListener('change',syncMode);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); removeEventListener('scroll',onScroll);removeEventListener('resize',schedule);view.removeEventListener('pointermove',move);view.removeEventListener('pointerleave',leave);document.removeEventListener('visibilitychange',schedule);media.removeEventListener('change',syncMode); };
  }, []);

  return <section className={`immersive-runway ${imagesReady ? 'is-loaded' : ''}`} ref={root} aria-label="From complexity to clarity" data-sc-act="flow">
    {header}
    <div className="immersive-stage" ref={stage}>
      <div className="immersive-world"><img src="/images/ciso-world.webp" alt="" onLoad={() => registerImage('world')} fetchPriority="high" /></div>
      <div className="immersive-portal"><img src="/images/ciso-portal.webp" alt="" onLoad={() => registerImage('portal')} /></div>
      <div className="immersive-wing immersive-wing--left" aria-hidden="true"><img src="/images/ciso-portal.webp" alt="" /></div>
      <div className="immersive-wing immersive-wing--right" aria-hidden="true"><img src="/images/ciso-portal.webp" alt="" /></div>
      <div className="immersive-top-shade" />
      <div className="immersive-opening">
        <div className="craft-label">Cybersecurity consulting · Fractional & virtual CISO services</div>
        <h1>Cybersecurity consulting that moves from <em>strategy</em> to execution.</h1>
        <p>CISO Operator helps growing and operationally complex businesses identify the risks that matter, implement the right protections, and make progress visible—with senior leadership matched to the work.</p>
        <a className="button button--teal" href="#contact">Request a security call <ArrowRight size={17} /></a>
        <a className="immersive-text-button" href="#method">See how the engagement works <ArrowRight size={16} /></a>
      </div>
      <div className="immersive-bridge" aria-hidden={phase !== 1}>
        <span className="craft-label">Senior-led engagements · Certified specialist support</span>
        <h2>Board-level judgment.<br /><em>Operator-level execution.</em></h2>
        
      </div>
      <div className="immersive-arrival" aria-hidden={!staticMode && phase !== 2}>
        <span className="craft-label">Begin with what the evidence supports</span>
        <h2>Senior judgment.<br /><em>Working protection.</em></h2>
        <p>Security products are plentiful. Accountable execution is scarce.</p>
      </div>
      <div className="immersive-deck" aria-label="CISO Operator services">
        {offers.map((offer, i) => <a className="immersive-card" href={offer.href} key={offer.title} onFocus={() => { if (!staticMode && chosen.current !== i) jumpRef.current(.73 + i / 3 * .25); }}>
          <span className="immersive-card__top"><span>{offer.label}</span><ArrowRight size={20} /></span>
          <div><h3>{offer.title}</h3><p>{offer.text}</p></div>
        </a>)}
      </div>
      <div className="immersive-footer">
        <div className="immersive-waypoints" aria-label="Experience navigation">{['Cybersecurity consulting', 'Leadership', 'Services'].map((label,i)=><button key={label} className={phase === i ? 'is-current' : ''} aria-current={phase === i ? 'step' : undefined} onClick={()=>jumpRef.current([0,.43,.73][i])}>{label}</button>)}</div>
        {phase === 2 && <div className="immersive-deck-controls"><button onClick={()=>jumpRef.current(.73+clamp(active-1,0,3)/3*.25)} disabled={active===0} aria-label="Previous service"><ArrowLeft size={18}/></button><span>{offers[active].title}</span><button onClick={()=>jumpRef.current(.73+clamp(active+1,0,3)/3*.25)} disabled={active===3} aria-label="Next service"><ArrowRight size={18}/></button></div>}
        <a className="immersive-skip" href="#services">All services <ChevronRight size={14}/></a>
      </div>
    </div>
    <noscript><style>{`.immersive-runway {height:auto!important}.immersive-stage{position:relative!important;min-height:800px}.immersive-opening{opacity:1!important;visibility:visible!important}.immersive-bridge,.immersive-arrival,.immersive-deck,.immersive-wing,.immersive-waypoints{display:none!important}`}</style></noscript>
  </section>;
}
